import React, { useEffect, FormEvent, useState, RefObject } from 'react';
import useTranslate from '@src/i18n/useTranslate';
import { LOGIN, SIGNUP, FORGOT_PASSWORD } from './AuthDisplay';
import SpinnerBar from '../reusable/spinner/SpinnerBar';
import Input from './InputComponents/Input';
import styled from 'styled-components';
import {
  AuthContainer,
  AuthForm,
  AuthHeader,
  AuthHeaderTitle,
  AuthInputsGrid,
  AuthInputContainer,
  AuthButtonGroup,
  AuthPrimaryButton,
  AuthSecondaryButton,
  AuthPreviousStepButton,
  AuthValidation,
} from './StyledComponents/AuthStyled';
import { LayoutColors } from '@src/core/theme';
import { useAppDispatch, useAppSelector } from '../../core/hooks';
import { setStepCheckout } from '@src/features/checkout';
import InputCheckboxTermsAndPolicy from './InputComponents/InputCheckboxTermsAndPolicy';
import {
  errorsSelector,
  hasEmailUniquenessErrorSelector,
  pendingSignupSelector,
  setErrors,
  signup,
  appInfoSelector,
  resetPassword,
  featureFlagSelector,
} from '@src/features/auth';
import { extractValues, getErrorMessage } from '../../common/index';
import { useWidgetId } from '../../core/IdProvider';
import InputPassword from './InputComponents/InputPassword';
import MaskedInput from './InputComponents/MaskedInput';
import { track } from '@src/services/analytics';
import ReCAPTCHA from 'react-google-recaptcha';
import Radio from '../ui2/Radio';
import Checkbox from '../ui2/Checkbox';
import NationalitySelect from '../reusable/NationalitySelect';
import { CountryCode, getCepAddress, initialAddressValues } from '@src/common/auth';
import countries from '@src/data/countries.json';
import OldPhoneInput from '../reusable/PhoneInput';
import { CountryIso2, PhoneInputRefType } from 'react-international-phone';
import { BodyRegularSemiBold } from '@src/fonts/typography/Typography';
import { PhoneInputOnchangeData } from '@src/common/phoneInput';
import { PhoneInput } from '@planne-software/uni/PhoneInput';
import { ScopedCssBaseline } from '@planne-software/uni/mui/material';
import { Address } from '@src/core/buyerApiClient';
import FeatureFlag from '../FeatureFlag';
import { parse, format } from 'date-fns';

interface SignupProps {
  setMode: (mode: string) => void;
}

const Signup = ({ setMode }: SignupProps) => {
  const id = useWidgetId();
  const dispatch = useAppDispatch();
  const { i18n, i18nElement } = useTranslate();

  const errors = useAppSelector(errorsSelector);
  const hasEmailUniquenessError = useAppSelector(hasEmailUniquenessErrorSelector);
  const pendingSignup = useAppSelector(pendingSignupSelector);
  const appInfo = useAppSelector(appInfoSelector);
  const newPhoneInputFeatureFlag = useAppSelector(featureFlagSelector('PR-1858-new-phone-input'));

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [identityType, setIdentityType] = useState<string>('cpf');
  const [isForeign, setIsForeign] = useState(false);
  const [nationality, setNationality] = useState<string>(CountryCode.ARGENTINA);
  const [foreignIdentityType, setForeignIdentityType] = useState<string>('passport');
  const [latestEmail, setLatestEmail] = useState<string | null>(null);

  const [phone, setPhone] = useState('+55');
  const [phoneDialCode, setPhoneDialCode] = useState('55');
  const phoneRef = React.createRef<HTMLInputElement>();

  useEffect(() => {
    dispatch(setStepCheckout({ id, value: SIGNUP }));
    dispatch(setErrors(SIGNUP, {}));

    track('signupStarted');
  }, []);

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const values = extractValues(ev.target as HTMLFormElement);

    track('signupSubmit');

    let phone = values.phone;

    if (newPhoneInputFeatureFlag) {
      phone = `+${phoneDialCode}${values.phone}`;
    }

    const zipCode = values.zipCode?.replace(/[^0-9]/g, '');
    let address: Address | undefined = {
      ...initialAddressValues,
      zipCode: zipCode,
    };
    const countryCode = values.countryCode ?? CountryCode.BRAZIL;

    if (!isForeign && zipCode) {
      try {
        const brasilApiAddress = await getCepAddress((zipCode ?? '').replace(/[^0-9]/g, ''));
        address = brasilApiAddress;
      } catch (error) {
        console.error(error);
      }
    }

    if (zipCode && countryCode === CountryCode.BRAZIL && zipCode?.length < 8) {
      dispatch(
        setErrors(SIGNUP, {
          zipCode: [i18n('auth_zipCode_max_length_error')],
        }),
      );
      return;
    }

    dispatch(
      signup({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        identity: values.identity.replace(/[^0-9a-zA-Z]/g, ''),
        identityType: isForeign ? foreignIdentityType : identityType,
        countryCode: countryCode,
        corporateName: values.corporateName ?? null,
        birthdate: values.birthdate ? format(parse(values.birthdate, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd') : null,
        phone: phone,
        agreesToTerms: values.agreesToTerms === 'on',
        recaptchaToken,
        address: {
          ...(isForeign || !address
            ? {
                ...initialAddressValues,
                zipCode: values.zipCode?.replace(/[^0-9]/g, '') || null,
              }
            : address),
        },
      }),
    );
  };

  const handleGoBackButtonClick = () => {
    track('signupBackButtonClick');

    setMode(LOGIN);
  };

  const handleAlreadyHaveAccountClick = () => {
    track('signupHaveAccountClick');

    setMode(LOGIN);
  };

  const handleChangeIdentityType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdentityType(e.target.value);
    setIsForeign(false);
  };

  const handleChangeIsForeign = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsForeign(e.target.checked);
  };

  const handleChangeNationality = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNationality(e.target.value);
    const country = countries.find((c) => c.alpha3 === e.target.value);

    if ([CountryCode.ARGENTINA, CountryCode.URUGUAY, CountryCode.PARAGUAY].includes(e.target.value as CountryCode)) {
      setForeignIdentityType('identity');
    } else {
      setForeignIdentityType('passport');
    }

    (phoneRef as RefObject<PhoneInputRefType>).current?.setCountry(country?.alpha2.toLocaleLowerCase() as CountryIso2);
  };

  const handlePasswordRecoverShortcut = (ev: FormEvent<HTMLButtonElement>) => {
    ev.preventDefault();

    track('uniqueEmailPasswordRecover');

    if (latestEmail) {
      setMode(FORGOT_PASSWORD);
      dispatch(resetPassword({ email: latestEmail }));
    }
  };

  const handleChangeForeignDocumentType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForeignIdentityType(e.target.value);
  };

  const showPassportOption = ![CountryCode.BRAZIL].includes(nationality as CountryCode);
  const showNationalDocumentOption = [CountryCode.ARGENTINA, CountryCode.URUGUAY, CountryCode.PARAGUAY].includes(
    nationality as CountryCode,
  );
  const handleChangePhone = (e: { target: { name: string; value: string } }, data?: PhoneInputOnchangeData) => {
    setPhone(e.target.value);
    setPhoneDialCode(data?.country.dialCode ?? '55');
  };

  return (
    <AuthContainer>
      <SpinnerBarArea visible={pendingSignup}>
        <SpinnerBar />
      </SpinnerBarArea>

      <AuthForm key={SIGNUP} onSubmit={handleSubmit} autoComplete='off'>
        <AuthHeader>
          <AuthHeaderTitle>{i18n('auth_createAccount')}</AuthHeaderTitle>
        </AuthHeader>

        <AuthInputsGrid>
          <IdentityTypeSelector>
            <Radio
              name='identityType'
              value='cpf'
              label={i18n('auth_identity_type_cpf')}
              checked={identityType === 'cpf'}
              onChange={handleChangeIdentityType}
            />
            <Radio
              name='identityType'
              value='cnpj'
              label={i18n('auth_identity_type_cnpj')}
              checked={identityType === 'cnpj'}
              onChange={handleChangeIdentityType}
            />
          </IdentityTypeSelector>

          <AuthInputContainer>
            <Input
              name='firstName'
              label={identityType === 'cpf' ? i18n('auth_labelName') : i18n('auth_cnpj_first_name')}
              errorContext={SIGNUP}
              maxlength={255}
            />
            <Input
              name='lastName'
              label={identityType === 'cpf' ? i18n('auth_labelSurname') : i18n('auth_cnpj_last_name')}
              errorContext={SIGNUP}
              maxlength={255}
            />
          </AuthInputContainer>

          <AuthInputContainer>
            <Input
              type='email'
              name='email'
              label={i18n('auth_labelEmail')}
              errorContext={SIGNUP}
              autoComplete={false}
              maxlength={255}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setLatestEmail(ev.target.value)}
            />
            <Input
              type='email'
              name='emailConfirmation'
              label={i18n('auth_mailConfirmation')}
              errorContext={SIGNUP}
              maxlength={255}
            />
          </AuthInputContainer>

          {hasEmailUniquenessError ? (
            <EmailUniquenessNotice>
              <h5>{i18n('auth_emailUniquenessError_title')}</h5>
              <p>{i18nElement('auth_emailUniquenessError_message')}</p>

              <CustomResetPasswordButton onClick={handlePasswordRecoverShortcut}>
                {i18n('auth_emailUniquenessError_action')}
              </CustomResetPasswordButton>
            </EmailUniquenessNotice>
          ) : null}

          {identityType === 'cnpj' ? (
            <AuthInputContainer>
              <Input name='corporateName' label={i18n('auth_corporate_name')} errorContext={SIGNUP} maxlength={255} />

              <MaskedInput
                name='identity'
                mask='99.999.999/9999-99'
                label={i18n('auth_identity_cnpj')}
                errorContext={SIGNUP}
              />
            </AuthInputContainer>
          ) : null}

          {identityType !== 'cnpj' && appInfo?.allowForeignBuyers ? (
            <AuthInputContainer>
              <Checkbox
                name='foreign'
                label={i18n('auth_is_foreign')}
                checked={isForeign}
                onChange={handleChangeIsForeign}
              />
            </AuthInputContainer>
          ) : null}

          {isForeign ? (
            <AuthInputContainer>
              <div>
                <NationalitySelect
                  name='countryCode'
                  label={i18n('auth_nationality')}
                  onChange={handleChangeNationality}
                  value={nationality}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Input name='identity' label={i18n('auth_document')} errorContext={SIGNUP} maxlength={255} />

                <IdentityTypeSelector>
                  {showNationalDocumentOption ? (
                    <Radio
                      name='foreignIdentityType'
                      value='identity'
                      label={i18n('auth_identity_national_document')}
                      onChange={handleChangeForeignDocumentType}
                      checked={foreignIdentityType === 'identity'}
                    />
                  ) : null}

                  {showPassportOption ? (
                    <Radio
                      name='foreignIdentityType'
                      value='passport'
                      label={i18n('auth_identity_passport')}
                      onChange={handleChangeForeignDocumentType}
                      checked={foreignIdentityType === 'passport'}
                    />
                  ) : null}
                </IdentityTypeSelector>
              </div>
            </AuthInputContainer>
          ) : identityType === 'cpf' ? (
            <MaskedInput
              name='identity'
              mask='999.999.999-99'
              label={i18n('auth_identity_cpf')}
              errorContext={SIGNUP}
            />
          ) : null}

          <AuthInputContainer>
            <FeatureFlag
              feature='PR-2195-collect-buyer-cep'
              active={() =>
                isForeign ? (
                  <Input name='zipCode' label={i18n('auth_cep')} errorContext={SIGNUP} />
                ) : (
                  <MaskedInput name='zipCode' label={i18n('auth_cep')} mask='99999-999' errorContext={SIGNUP} />
                )
              }
            />

            <FeatureFlag
              feature='PR-2195-collect-buyer-birthdate'
              active={() => (
                <MaskedInput //Change this to use the DatePicker from uni when it's available
                  name='birthdate'
                  mask='99/99/9999'
                  placeholder='DD/MM/YYYY'
                  pattern='^([0-2][0-9]|30|31)/(0[0-9]|10|11|12)/[0-9]{4}$'
                  label={i18n('auth_birthdate')}
                  errorContext={SIGNUP}
                />
              )}
            />
          </AuthInputContainer>

          <FeatureFlag
            feature='PR-1858-new-phone-input'
            fallback={() => (
              <OldPhoneInput
                name='phone'
                label={i18n('auth_cellphone')}
                value={phone}
                onChange={handleChangePhone}
                ref={phoneRef}
                error={!!getErrorMessage(errors[SIGNUP], 'phone')}
                helperText={getErrorMessage(errors[SIGNUP], 'phone')}
              />
            )}
            active={() => (
              <ScopedCssBaseline>
                <PhoneInput
                  name='phone'
                  size='medium'
                  label={i18n('auth_cellphone')}
                  value={phone}
                  onChange={(value) => {
                    setPhone(value.phone);
                    setPhoneDialCode(value.country?.dialCode ?? '55');
                  }}
                  ref={phoneRef}
                  error={!!getErrorMessage(errors[SIGNUP], 'phone')}
                  helperText={getErrorMessage(errors[SIGNUP], 'phone')}
                />
              </ScopedCssBaseline>
            )}
          />

          <AuthInputContainer>
            <InputPassword
              name='password'
              label={i18n('base_password')}
              errorContext={SIGNUP}
              autoComplete={false}
              showInfo={i18n('auth_signupPasswordInfo')}
            />
            <InputPassword
              name='passwordConfirmation'
              label={i18n('auth_passwordConfirmation')}
              errorContext={SIGNUP}
            />
          </AuthInputContainer>

          {appInfo?.recaptchaKey && (
            <RecaptchaContainer>
              <ReCAPTCHA sitekey={appInfo.recaptchaKey} onChange={setRecaptchaToken} />
              {getErrorMessage(errors[SIGNUP], 'recaptchaToken') ? (
                <AuthValidation>{i18n('recaptcha_validation_error')}</AuthValidation>
              ) : null}
            </RecaptchaContainer>
          )}

          <InputCheckboxTermsAndPolicy name={'agreesToTerms'} errorContext={SIGNUP} />
        </AuthInputsGrid>

        <AuthButtonGroup>
          <AuthPrimaryButton>{i18n('auth_createAccount')}</AuthPrimaryButton>

          <AuthSecondaryButton onClick={handleAlreadyHaveAccountClick}>
            {i18n('auth_alreadyHaveAnAccount')}
          </AuthSecondaryButton>

          <AuthPreviousStepButton onClick={handleGoBackButtonClick}>{i18n('auth_goBackButton')}</AuthPreviousStepButton>
        </AuthButtonGroup>
      </AuthForm>
    </AuthContainer>
  );
};

interface SpinnerBarAreaProps {
  visible: boolean;
}

const SpinnerBarArea = styled.div<SpinnerBarAreaProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  opacity: 0.5;
  background-color: ${LayoutColors.whiteLight};
  align-items: center;
  justify-content: center;
`;

const RecaptchaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  padding-top: 16px;
  ${BodyRegularSemiBold};
`;

const IdentityTypeSelector = styled.div`
  display: flex;
  gap: 16px;
`;

const EmailUniquenessNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 4px;
  background: #fff5e5;

  &,
  & h5,
  & p {
    color: #633903;
  }

  & h5 {
    font-size: 16px;
    font-weight: 700;
  }

  & p {
    font-weight: 500;
  }
`;

const CustomResetPasswordButton = styled(AuthPrimaryButton)`
  width: fit-content;
  text-transform: uppercase;
`;

export default Signup;
